/**
 * English translations
 */
import { TranslationStrings } from '../types';

export const en: TranslationStrings = {  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    cancel: 'Cancel',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    done: 'Done',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    required: 'Required',
    optional: 'Optional',
  },
  consent: {
    secureConsent: 'Secure Consent',
    yourConsentRequired: 'Your Consent is Required',
    consentingAs: 'Consenting as',
    requestingOrg: 'Requesting Organisation',
    dataBeingCollected: 'Data Being Collected',
    purposeOfProcessing: 'Purpose of Processing',
    whatYouWillDo: "What you will do",
    reviewDocuments: 'Review Documents',
    provideConsent: 'Consent Confirmation',
    documentsToRead: 'documents to read',
    estimatedTime: 'Estimated time',
    minutes: 'minutes',
    startReview: 'Start Review',
    yourRights: 'Your Rights Under DPDP Act 2023',
    rightToAccess: 'Right to access your data',
    rightToCorrect: 'Right to correct your data',
    rightToWithdraw: 'Right to withdraw consent',
    rightToGrievance: 'Right to file grievances',
    stepOf: 'Step {current} of {total}',
    pleaseReadCarefully: 'Please read the documents carefully before providing your consent.',
    documentsReviewed: 'documents reviewed',
    remaining: 'remaining',
    allDocumentsReviewed: 'All documents reviewed!',
    clickToRead: 'Click each document below to read it',
    proceedToConsent: 'You may proceed to provide your consent',
    markAsRead: 'Mark as Read',
    continueReading: 'Continue Reading',
    confirmConsent: 'Confirm Your Consent',
    selectMethod: 'Select verification method',
    iAgreeToTerms: 'I agree to all terms, policies, and consents',
    readAllDocuments: 'I understand that my consent is voluntary and can be withdrawn at any time',
    submitConsent: 'Submit Consent',
    submitting: 'Submitting...',
    digitalSignature: 'E-Signature based consent',
    smsVerification: 'OTP based consent',
    emailVerification: 'Email OTP based consent',
    videoRecording: 'Video based consent',
    audioRecording: 'Audio based consent',
    checkboxConfirmation: 'Checkbox based consent',
    drawSignature: 'Draw your signature',
    verifyViaSms: 'Verify via SMS code',
    verifyViaEmail: 'Verify via email code',
    recordVideoConsent: 'Record video consent',
    recordAudioConsent: 'Record audio consent',
    confirmByChecking: 'Confirm by checking',
    enterOtp: 'Enter OTP',
    otpSentTo: 'OTP sent to',
    resendOtp: 'Resend OTP',
    verifyOtp: 'Verify OTP',
    otpVerified: 'OTP Verified',
    invalidOtp: 'Invalid OTP',
    startRecording: 'Start Recording',
    stopRecording: 'Stop Recording',
    retake: 'Retake',
    recordingComplete: 'Recording Complete',
    consentSubmitted: 'Consent Recorded Successfully',
    thankYou: 'Your Consent has been securely recorded and will be processed according to the terms you agreed to.',
    confirmationSent: 'A confirmation has been sent to your email',
    referenceNumber: 'Reference Number',
    errorSubmitting: 'Failed to submit consent. Please try again.',
    pleaseReadDocs: 'Please read all documents first',
    pleaseProvideEvidence: 'Please provide consent evidence',
  },
  documents: {
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    consentAgreement: 'Consent',
    dataSharingAgreement: 'Data Sharing Agreement',
    cookiePolicy: 'Cookie Policy',
    retentionPolicy: 'Data Retention Policy',
    gdprNotice: 'GDPR Notice',
    hipaaAuthorization: 'HIPAA Authorization',
    ccpaDisclosure: 'CCPA Disclosure',
    aadhaarAuthorization: 'Aadhaar Authorization',
    customDocument: 'Custom Document',
  },
  dataCategories: {
    personalInfo: 'Personal Information',
    contactDetails: 'Contact Details',
    financialInfo: 'Financial Information',
    healthData: 'Health & Medical Data',
    biometricData: 'Biometric Data',
    locationData: 'Location Data',
    behavioralData: 'Behavioral Data',
    communicationData: 'Communication Data',
    employmentInfo: 'Employment Information',
    educationInfo: 'Educational Information',
  },
  a11y: {
    mainContent: 'Main content',
    navigation: 'Navigation',
    closeModal: 'Close modal',
    expandSection: 'Expand section',
    collapseSection: 'Collapse section',
    documentRead: 'Document marked as read',
    documentUnread: 'Document not yet read',
    progressIndicator: 'Progress: {current} of {total} documents read',
    signaturePad: 'Signature drawing area',
    clearSignature: 'Clear signature',
    videoPlayer: 'Video recording player',
    audioPlayer: 'Audio recording player',
    requiredField: 'This field is required',
    errorMessage: 'Error',
    successMessage: 'Success',
    loadingContent: 'Loading content, please wait',
    newWindow: 'Opens in new window',
  },
  portal: {
    // General
    myConsents: 'My Consents',
    viewManageConsents: 'View and manage your consent artifacts',
    logout: 'Logout',
    welcomeBack: 'Welcome back',
    
    // Tenant selector
    organizationNameOrCode: 'Organisation Name or Code',
    orgPlaceholder: 'e.g., globalhealth or acme-corp',
    orgHint: 'Enter the name or code provided by your organisation',
    findMyOrganization: 'Find My Organisation',
    orgNotFound: 'Organisation not found. Please check the name and try again.',
    enterOrgName: 'Please enter your organisation name or code',
    or: 'or',
    haveDirectLink: 'Have a direct link from your organisation?',
    useDirectLink: 'Use the link they provided (e.g., yoursite.com/t/orgname/my-consent/login)',
    privacyProtected: 'Your privacy is protected by law',
    
    // Login flow
    phoneOrEmail: 'Phone Number or Email',
    enterPhoneOrEmailPlaceholder: 'Enter your registered phone or email',
    otpWillBeSent: 'We will send you a one-time password (OTP)',
    sendOtp: 'Send OTP',
    enterPhoneOrEmail: 'Please enter your phone number or email',
    otpSentTo: 'OTP sent to',
    expiresIn: 'Expires in',
    enterOtp: 'Enter OTP',
    enterValidOtp: 'Please enter a valid OTP',
    verifyOtp: 'Verify OTP',
    resendOtp: 'Resend OTP',
    otpVerified: 'OTP Verified',
    oneMoreStep: 'One more step to verify your identity',
    enterSecurityAnswer: 'Please enter your answer',
    enterAnswer: 'Enter your answer',
    dobFormat: 'Format: DDMMYYYY (e.g., 15051990 for May 15, 1990)',
    login: 'Login',
    startOver: 'Start over',
    needHelp: 'Need help?',
    privacyNotice: 'Your data is protected under applicable privacy laws',
    checkUrlOrContact: 'Please check the URL or contact your organisation.',
    
    // Consent history
    filterAll: 'All',
    statusActive: 'Active',
    statusWithdrawn: 'Withdrawn',
    statusExpired: 'Expired',
    noConsents: 'No consent artifacts found',
    noConsentsWithFilter: 'No consents with this status',
    consentedOn: 'Consented on',
    expiresOn: 'Expires on',
    withdrawnOn: 'Withdrawn on',
    withdrawConsent: 'Withdraw Consent',
    withdrawalHistory: 'Withdrawal History',
    help: 'Help',
    
    // Withdrawal
    cannotWithdraw: 'This consent cannot be withdrawn',
    pleaseConfirmWithdrawal: 'Please confirm that you understand the implications',
    importantNotice: 'Important Notice',
    withdrawalNotice1: 'The organisation will stop processing your data for the stated purposes.',
    withdrawalNotice2: 'Some data may be retained for legal compliance.',
    withdrawalNotice3: 'You may need to provide consent again to use certain services.',
    withdrawalNotice4: 'Processing time may take 7-30 days depending on applicable laws.',
    withdrawalReason: 'Reason for withdrawal',
    withdrawalReasonPlaceholder: 'Tell us why you want to withdraw (optional)',
    withdrawalConfirmation: 'I understand that withdrawing my consent may affect my ability to use certain services, and I wish to proceed with this request.',
    submitWithdrawalRequest: 'Submit Withdrawal Request',
    withdrawalSubmitted: 'Withdrawal Request Submitted',
    withdrawalSubmittedDesc: 'Your withdrawal request has been submitted. You will be notified once it is processed by the organisation.',
    processingTime: 'Processing time',
    days: 'days',
    backToHistory: 'Back to Consent History',
    
    // Withdrawal history
    statusPending: 'Pending',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    noWithdrawals: 'No withdrawal requests yet',
    viewConsents: 'View your consents',
    requestedOn: 'Requested on',
    processedOn: 'Processed on',
    processingMessage: 'Your request is being processed. You will be notified once completed.',
    processingTimeInfo: 'Processing Time Information',
    
    // Help section
    yourRights: 'Your Privacy Rights',
    helpTitle: 'Understanding Your Privacy Rights',
    helpSubtitle: 'Learn about your rights under privacy laws and how to manage your consent.',
    quickActions: 'Quick Actions',
    viewMyConsents: 'View My Consents',
    viewWithdrawalRequests: 'View Withdrawal Requests',
    loginToViewConsents: 'Login to View Your Consents',
    applicableLaws: 'Applicable Privacy Laws',
    
    // Rights FAQ
    rightAccessQ: 'What is the right to access?',
    rightAccessA: 'You have the right to know what personal data is being collected about you, how it is being used, and who it is being shared with. You can request a copy of your data at any time.',
    rightCorrectQ: 'What is the right to correction?',
    rightCorrectA: 'If your personal data is inaccurate or incomplete, you can request that it be corrected or updated.',
    rightWithdrawQ: 'What is the right to withdraw consent?',
    rightWithdrawA: 'You can withdraw your consent at any time. Once withdrawn, the organisation must stop processing your data for the purposes you consented to. Some data may be retained for legal compliance.',
    rightEraseQ: 'What is the right to erasure?',
    rightEraseA: 'Also known as the "right to be forgotten", you can request that your personal data be deleted. However, there may be legal reasons why the organisation needs to retain some information.',
    
    // Withdrawal FAQ
    withdrawalFaq: 'Withdrawal Process',
    withdrawHowQ: 'How do I withdraw my consent?',
    withdrawHowA: 'Log in to your account, go to your consent history, and click "Withdraw Consent" on any active consent. You may optionally provide a reason for withdrawal.',
    withdrawTimeQ: 'How long does withdrawal take?',
    withdrawTimeA: 'Processing times depend on applicable laws: DPDP Act (India) - 7 days, GDPR (EU) - 30 days, CCPA (California) - 45 days. You will be notified when the process is complete.',
    withdrawEffectQ: 'What happens after I withdraw consent?',
    withdrawEffectA: 'The organisation will stop using your data for the stated purposes. Some data may be retained for legal compliance. You may lose access to certain services that required your consent.',
    
    // Security FAQ
    securityFaq: 'Account Security',
    loginSecureQ: 'How is my login secured?',
    loginSecureA: 'We use a 3-step verification process: OTP sent to your registered phone/email, followed by a security question. This ensures only you can access your consent artifacts.',
    dataProtectQ: 'How is my data protected?',
    dataProtectA: 'All data is encrypted in transit and at rest. We follow industry best practices and comply with DPDP Act, GDPR, and other applicable privacy regulations.',
    
    // Contact FAQ
    contactFaq: 'Contact & Support',
    grievanceQ: 'How do I file a grievance?',
    grievanceA: 'You can contact the organisation\'s Data Protection Officer (DPO) or Grievance Officer. Their contact details should be provided in the privacy policy you consented to.',
    regulatorQ: 'Who is the regulatory authority?',
    regulatorA: 'In India, the Data Protection Board handles privacy complaints. In the EU, you can contact your national Data Protection Authority. In California, the Attorney General handles CCPA complaints.',
  },
}
;
